/**
 * Störungskategorien-Verwaltung - Liste
 * 
 * Funktionen:
 * - Liste aller Störungskategorien
 * - Navigation zu Detailseite (Create/Edit)
 * 
 * UX-Leitlinie: Keine Modals für Create/Edit, nur Liste → Detailseite
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, RefreshCw, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface DisruptionCategory {
  id: string;
  code: string;
  name: string;
  color: string | null;
  isActive: boolean;
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function DisruptionCategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<DisruptionCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/disruption-categories', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Störungskategorien:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const filtered = sortAlpha(categories.filter(c => c.isActive !== false));

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Störungskategorien
            </h1>
            <p className="text-sm text-slate-500 mt-1">{filtered.length} aktive Kategorien</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadCategories}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => navigate('/admin/disruption-categories/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Kategorie
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Keine Störungskategorien vorhanden
              <button 
                onClick={() => navigate('/admin/disruption-categories/new')}
                className="block mx-auto mt-4 text-blue-600 hover:underline"
              >
                Erste Kategorie anlegen
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Code</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(c => (
                  <tr 
                    key={c.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/disruption-categories/${c.id}`)}
                  >
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-mono bg-slate-100 text-slate-700 rounded">
                        {c.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
